import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { calcGameStats, calcRoundWinners } from '../../utils/scoring';
import { getRounds, getAllScores } from '../../services/firebase/games';
import { finalizeGameStats, renameGame } from '../../services/firebase/users';
import { useAuth } from '../../context/AuthContext';
import { useWebRTC } from '../../context/WebRTCContext';
import { CHATTERS_ID } from '../../constants/game';
import { logError } from '../../utils/logger';
import PlayerVideoCard from '../../components/PlayerVideoCard';

const LAST_GAME_KEY = 'tmg_last_game';

const DATE_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/** Round stats table matching the profile game-history layout. */
function GameOverRoundTable({ round, scores, playerIds, playerNames }) {
  const { t } = useTranslation();
  const { song, chooserId, roundNumber } = round;
  const winners = calcRoundWinners(playerIds, scores);

  return (
    <div className="px-5 py-4">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="min-w-0">
          <p className="text-xs t-text-disabled mb-0.5">{t('profile.round', { n: roundNumber })}</p>
          <p className="font-semibold t-text truncate">{song?.title ?? t('profile.unknownSong')}</p>
          <p className="text-sm t-text-muted truncate">{song?.artist}</p>
        </div>
        <p className="text-xs t-text-disabled shrink-0">
          {t('profile.chosenBy', { name: playerNames[chooserId] ?? chooserId })}
        </p>
      </div>
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-sm min-w-0">
          <caption className="sr-only">
            {t('profile.roundHistoryCaption', { n: roundNumber })}
          </caption>
          <thead>
            <tr className="text-xs t-text-disabled t-border border-b">
              <th className="text-left pb-2 pl-1 font-medium">{t('profile.playerTableHeader')}</th>
              <th className="text-center pb-2 px-3 font-medium">{t('profile.scoreTableHeader')}</th>
              <th className="text-left pb-2 px-3 font-medium">{t('profile.predictionsTableHeader')}</th>
              <th className="text-right pb-2 pr-1 font-medium">{t('profile.errorTableHeader')}</th>
            </tr>
          </thead>
          <tbody>
            {playerIds.map((uid) => {
              const data = scores[uid] ?? {};
              const actualScore = data.actualScore;
              const predictions = data.predictions ?? {};
              const isWinner = winners.includes(uid);

              const predErrors = Object.entries(predictions)
                .filter(([targetUid]) => targetUid !== uid)
                .map(([targetUid, pred]) => {
                  const actual = scores[targetUid]?.actualScore;
                  return actual !== undefined ? Math.abs(pred - actual) : null;
                })
                .filter((e) => e !== null);
              const avgErr =
                predErrors.length > 0
                  ? `${Math.round((1 - (predErrors.reduce((a, b) => a + b, 0) / predErrors.length) / 10) * 100)}%`
                  : '-';

              const predText = Object.entries(predictions)
                .filter(([targetUid]) => targetUid !== uid)
                .map(([targetUid, pred]) => `${playerNames[targetUid] ?? targetUid}: ${pred}`)
                .join(', ');

              return (
                <tr
                  key={uid}
                  className={`t-border border-b last:border-0 ${isWinner ? 't-text-highlight' : 't-text'}`}
                >
                  <td className="py-2 pl-1">
                    <span className="font-medium">{playerNames[uid] ?? uid}</span>
                    {isWinner && (
                      <span className="ml-1 text-xs" aria-label="round winner">🏆</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-center">
                    {actualScore !== undefined ? actualScore : '-'}
                  </td>
                  <td className="py-2 px-3 t-text-muted text-xs">{predText || '-'}</td>
                  <td className="py-2 pr-1 text-right t-text-disabled text-xs">{avgErr}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Camera card for category winners. */
function WinnerDisplay({ uid, name, avatarEmoji, webrtc, myUid, isViewerChatter }) {
  if (!webrtc) {
    return (
      <span className="text-3xl select-none" aria-hidden="true" title={name}>
        {avatarEmoji || '🎵'}
      </span>
    );
  }
  const isMe = uid === myUid;
  return (
    <div className="w-32 shrink-0">
      <PlayerVideoCard
        uid={uid}
        name={name}
        avatarEmoji={avatarEmoji}
        isMe={isMe}
        isViewerChatter={isViewerChatter}
        compactMediaButtons
        mediaState={webrtc.playerMediaStates?.[uid]}
        localCameraStream={isMe ? webrtc.localCameraStream : null}
        remoteStreamsByUid={!isMe ? webrtc.remoteStreamsByUid?.get(uid) : null}
        cameraStreamId={webrtc.playerMediaStates?.[uid]?.cameraStreamId}
        myCameraEnabled={isMe ? webrtc.cameraEnabled : undefined}
        myMicEnabled={isMe ? webrtc.micEnabled : undefined}
        toggleCamera={isMe ? webrtc.toggleCamera : undefined}
        toggleMic={isMe ? webrtc.toggleMic : undefined}
        isMuted={!isMe ? (webrtc.localMutes?.[uid] ?? false) : undefined}
        volume={!isMe ? (webrtc.localVolumes?.[uid] ?? 1) : undefined}
        isHidden={!isMe ? (webrtc.localHidden?.[uid] ?? false) : undefined}
        onMuteToggle={!isMe ? () => webrtc.localMutes?.[uid] ? webrtc.unmuteRemote?.(uid) : webrtc.muteRemote?.(uid) : undefined}
        onVolumeChange={!isMe ? (v) => webrtc.setRemoteVolume?.(uid, v) : undefined}
        onHideToggle={!isMe ? () => webrtc.localHidden?.[uid] ? webrtc.showRemoteCamera?.(uid) : webrtc.hideRemoteCamera?.(uid) : undefined}
        onKick={!isMe && webrtc.kickPlayer ? () => webrtc.kickPlayer(uid) : undefined}
        renderTopLeft={() => (
          <p className="t-text text-xs font-medium leading-tight drop-shadow truncate">
            {name}
          </p>
        )}
      />
    </div>
  );
}

export default function GameOver({ game, isChatter, playerAvatars }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const webrtc = useWebRTC();
  const hasWebRTC = !!webrtc && !game.inPersonMode;

  const [stats, setStats] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [allScores, setAllScores] = useState({});
  const [allRoundsOpen, setAllRoundsOpen] = useState(false);
  const statsUpdated = useRef(false);

  // Game rename state
  const [gameName, setGameName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);
  const [nameError, setNameError] = useState('');
  // Show rename UI after a brief delay to give the CF time to write the document
  const [showRename, setShowRename] = useState(false);

  useEffect(() => {
    async function load() {
      const [r, s] = await Promise.all([getRounds(game.id), getAllScores(game.id)]);
      const sorted = r.sort((a, b) => a.roundNumber - b.roundNumber);

      const anyRoundCompleted = sorted.some((r) => r.phase === 'round_results');

      if (!anyRoundCompleted) {
        // No rounds completed — clean up the game document and go back to lobby
        if (user && !statsUpdated.current && !isChatter) {
          statsUpdated.current = true;
          finalizeGameStats(game.id).catch(() => {}); // CF will delete the game doc
        }
        localStorage.removeItem(LAST_GAME_KEY);
        navigate('/lobby');
        return;
      }

      setRounds(sorted);
      setAllScores(s);
      const computed = calcGameStats(game.playerIds, sorted, s);
      setStats(computed);

      if (user && !statsUpdated.current && !isChatter) {
        statsUpdated.current = true;
        finalizeGameStats(game.id).catch(() => {});
      }
    }
    load();

    localStorage.removeItem(LAST_GAME_KEY);

    const timer = setTimeout(() => {
      if (!isChatter) {
        setGameName(DATE_FORMAT.format(new Date()));
        setShowRename(true);
      }
    }, 4000);
    return () => clearTimeout(timer);
  }, [game.id, game.playerIds, user, isChatter, navigate]);

  async function handleSaveName() {
    if (!gameName.trim() || !user) return;
    setSavingName(true);
    setNameError('');
    try {
      await renameGame(user.uid, game.id, gameName.trim());
      setNameSaved(true);
    } catch (err) {
      logError(err);
      setNameError(t('gameOver.couldntSaveName'));
    } finally {
      setSavingName(false);
    }
  }

  if (!stats) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-8 h-8 border-2 t-border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const regularPlayerIds = game.playerIds.filter((id) => id !== CHATTERS_ID);

  const categories = [
    {
      emoji: '🏆',
      title: t('gameOver.categories.officialWinner'),
      subtitle: t('gameOver.categories.officialWinnerSub'),
      uids: stats.mostRoundWins,
      detail: (uid) => t('gameOver.detail.roundsWon', { count: stats.roundWins[uid] }),
    },
    {
      emoji: '🔮',
      title: t('gameOver.categories.bestPredictor'),
      subtitle: t('gameOver.categories.bestPredictorSub'),
      uids: stats.bestPredictor,
      detail: (uid) => { const e = stats.avgPredError[uid]; return t('gameOver.detail.avgError', { value: e != null ? `${Math.round((1 - e / 10) * 100)}%` : '-' }); },
    },
    {
      emoji: '🙈',
      title: t('gameOver.categories.worstPredictor'),
      subtitle: t('gameOver.categories.worstPredictorSub'),
      uids: stats.worstPredictor,
      detail: (uid) => { const e = stats.avgPredError[uid]; return t('gameOver.detail.avgError', { value: e != null ? `${Math.round((1 - e / 10) * 100)}%` : '-' }); },
    },
    {
      emoji: '🌑',
      title: t('gameOver.categories.mostMysterious'),
      subtitle: t('gameOver.categories.mostMysteriousSub'),
      uids: stats.mostMysterious,
      detail: (uid) => { const e = stats.avgBeingPredError[uid]; return t('gameOver.detail.avgErrorOthers', { value: e != null ? `${Math.round((1 - e / 10) * 100)}%` : '-' }); },
    },
    {
      emoji: '📖',
      title: t('gameOver.categories.mostPredictable'),
      subtitle: t('gameOver.categories.mostPredictableSub'),
      uids: stats.mostPredictable,
      detail: (uid) => { const e = stats.avgBeingPredError[uid]; return t('gameOver.detail.avgErrorOthers', { value: e != null ? `${Math.round((1 - e / 10) * 100)}%` : '-' }); },
    },
    {
      emoji: '🎸',
      title: t('gameOver.categories.bestTaste'),
      subtitle: t('gameOver.categories.bestTasteSub'),
      uids: stats.bestTaste,
      detail: (uid) => t('gameOver.detail.avgSongRating', { value: stats.chooserAvg[uid]?.toFixed(1) ?? '-' }),
    },
    {
      emoji: '🤢',
      title: t('gameOver.categories.worstTaste'),
      subtitle: t('gameOver.categories.worstTasteSub'),
      uids: (stats.worstTaste ?? []).filter((uid) => !(stats.bestTaste ?? []).includes(uid)),
      detail: (uid) => t('gameOver.detail.avgSongRating', { value: stats.chooserAvg[uid]?.toFixed(1) ?? '-' }),
    },
  ];

  // Wooden Spoon: regular players who didn't win any of the above categories
  const allCategoryWinners = new Set(categories.flatMap((c) => c.uids));
  const woodenSpoonUids = regularPlayerIds.filter((uid) => !allCategoryWinners.has(uid));
  if (woodenSpoonUids.length > 0) {
    categories.push({
      emoji: '🥄',
      title: t('gameOver.categories.woodenSpoon'),
      subtitle: t('gameOver.categories.woodenSpoonSub'),
      uids: woodenSpoonUids,
      detail: null,
    });
  }

  const songCategories = [
    { emoji: '⭐', title: t('gameOver.categories.bestSong'), songs: stats.bestSongs },
    { emoji: '💀', title: t('gameOver.categories.worstSong'), songs: stats.worstSongs },
  ];

  return (
    <div className="flex-1 flex flex-col items-center px-4 py-8 overflow-y-auto">
      <div className="w-full max-w-lg space-y-5">
        <div className="text-center">
          <div className="text-5xl mb-3" aria-hidden="true">🎉</div>
          <h2 className="text-2xl font-bold t-text">{t('gameOver.heading')}</h2>
          <p className="t-text-muted mt-1">{t('gameOver.roundsPlayed', { count: rounds.length })}</p>
        </div>

        {/* Player award categories */}
        <div className="space-y-3">
          {categories.map(({ emoji, title, subtitle, uids, detail }) => {
            if (uids.length === 0) return null;

            return (
              <div
                key={title}
                className="t-bg-surface t-border border rounded-2xl px-5 py-4 flex items-center gap-4"
              >
                <span className="text-2xl shrink-0" aria-hidden="true">{emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm t-text-muted">{title}</p>
                  {subtitle && <p className="text-xs t-text-disabled">{subtitle}</p>}
                  <p className="font-bold t-text mt-0.5">
                    {uids.map((uid) => game.playerNames?.[uid] || uid).join(' & ')}
                  </p>
                  {detail && (
                    <p className="text-xs t-text-disabled mt-0.5">
                      {uids.map((uid) => detail(uid)).join(' / ')}
                    </p>
                  )}
                </div>
                {/* Camera thumbnails */}
                {hasWebRTC && (
                  <div className="flex gap-1.5 shrink-0">
                    {uids.map((uid) => (
                      <WinnerDisplay
                        key={uid}
                        uid={uid}
                        name={game.playerNames?.[uid] || uid}
                        avatarEmoji={playerAvatars?.[uid]}
                        webrtc={webrtc}
                        myUid={user?.uid}
                        isViewerChatter={isChatter}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Song categories */}
        {songCategories.map(({ emoji, title, songs }) =>
          songs.length > 0 ? (
            <div
              key={title}
              className="t-bg-surface t-border border rounded-2xl px-5 py-4 flex items-start gap-4"
            >
              <span className="text-2xl shrink-0 mt-0.5" aria-hidden="true">{emoji}</span>
              <div>
                <p className="text-sm t-text-muted">{title}</p>
                {songs.map((r) => (
                  <div key={r.id} className="mt-1">
                    <p className="font-bold t-text">{r.song?.title}</p>
                    <p className="text-sm t-text-muted">{r.song?.artist}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null
        )}

        {/* All rounds — collapsible table matching profile game history */}
        {rounds.length > 0 && (
          <div className="t-bg-surface t-border border rounded-2xl overflow-hidden">
            <button
              onClick={() => setAllRoundsOpen((v) => !v)}
              className="w-full px-5 py-4 flex items-center justify-between text-left t-focus-ring"
              aria-expanded={allRoundsOpen}
            >
              <span className="text-sm font-semibold t-text-muted">{t('gameOver.allRounds')}</span>
              <span className="t-text-disabled text-xs ml-4" aria-hidden="true">
                {allRoundsOpen ? '▲' : '▼'}
              </span>
            </button>
            {allRoundsOpen && (
              <div className="t-border border-t divide-y divide-(--c-border)">
                {rounds.map((round) => (
                  <GameOverRoundTable
                    key={round.id}
                    round={round}
                    scores={allScores[round.id] || {}}
                    playerIds={game.playerIds}
                    playerNames={game.playerNames || {}}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Game rename */}
        {showRename && (
          <div className="t-bg-surface t-border border rounded-2xl p-5 space-y-3">
            <h3 className="text-sm font-semibold t-text-muted">{t('gameOver.nameThisGame')}</h3>
            <p className="text-xs t-text-disabled">
              {t('gameOver.nameHint')}
            </p>
            {nameSaved ? (
              <p role="status" aria-live="polite" className="text-sm t-text-success">{t('gameOver.saved')}</p>
            ) : (
              <div className="flex gap-2">
                <label htmlFor="game-name-input" className="sr-only">{t('gameOver.gameNameLabel')}</label>
                <input
                  id="game-name-input"
                  type="text"
                  value={gameName}
                  onChange={(e) => setGameName(e.target.value)}
                  maxLength={53}
                  className="flex-1 t-bg-input t-border-input border rounded-lg px-3 py-2 t-text text-sm t-placeholder t-focus-input"
                />
                <button
                  onClick={handleSaveName}
                  disabled={savingName || !gameName.trim()}
                  className="t-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-4 py-2 text-sm transition-colors t-focus-ring"
                >
                  {savingName ? t('gameOver.saving') : t('gameOver.save')}
                </button>
              </div>
            )}
            {nameError && (
              <p role="alert" aria-live="polite" className="text-xs t-text-error">{nameError}</p>
            )}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => navigate('/lobby')}
            className="flex-1 t-btn-primary font-semibold rounded-lg px-4 py-3 transition-colors t-focus-ring"
          >
            {t('gameOver.backToLobby')}
          </button>
          {user && game.playerNames?.[user.uid] && (
            <Link
              to={`/profile/${game.playerNames[user.uid]}`}
              className="flex-1 text-center t-bg-input hover:opacity-80 t-text font-semibold rounded-lg px-4 py-3 transition-colors t-focus-ring"
            >
              {t('gameOver.viewProfile')}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
